import { useMemo } from 'react';

import { gql } from '@soundxyz/gql-string';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import {
  AudienceV2Document,
  MinimalReleaseUpdatesDocument,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import type { ReleaseCollectorCursorConnectionSort, TierNft } from '@graphql/reactQuery';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { useLogError } from '@hooks/useLogErrorNew';

import {
  MOBILE_MAX_SEATS_V2,
  TABLET_MAX_SEATS_V2,
  TABLET_SIZE,
} from '../Audience/shared/constants';

import type { AudienceSortType } from './constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AudienceV2($releaseId: UUID!, $pagination: ReleaseCollectorCursorConnectionArgs!) {
    release(id: $releaseId) {
      id
      collectors(pagination: $pagination) {
        edges {
          node {
            id
            nftsCount
            vipNftsCount
            gaNftsCount
            lowestNftSerialNumber
            user {
              id
              publicAddress
            }
            ...TopAudienceSeat
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

function getSortByTier(
  tier: TierNft,
  sort: AudienceSortType,
): ReleaseCollectorCursorConnectionSort | undefined {
  const isTop = sort === 'top';
  const earliest: ReleaseCollectorCursorConnectionSort = {
    priorityFirstNftDate: 'ASC',
  };

  switch (tier) {
    case 'GA': {
      return isTop
        ? {
            gaNftsCount: 'DESC',
          }
        : earliest;
    }
    case 'ALL':
      return isTop
        ? {
            priorityTop: 'DESC',
          }
        : earliest;
    // "vipNftsCount" sort has tie breaker on total amount of nfts (w/ ga), but we always prioritize vip nfts
    case 'VIP': {
      return isTop
        ? {
            vipNftsCount: 'DESC',
          }
        : earliest;
    }
    default: {
      return;
    }
  }
}

RefetchOperationsOnComplete({
  trigger: MinimalReleaseUpdatesDocument,
  refetch: AudienceV2Document,
  throttled: '5 seconds',
});

export function useAudienceV2({
  releaseId,
  tier,
  sort,
}: {
  releaseId: string;
  tier: TierNft;
  sort: AudienceSortType;
}) {
  const logError = useLogError();
  const isHydrated = useIsHydrated();

  const maxSeats =
    isHydrated && global.window.innerWidth < TABLET_SIZE
      ? MOBILE_MAX_SEATS_V2
      : TABLET_MAX_SEATS_V2;

  const {
    orderedList: collectors,
    error,
    isLoadingNewPage: loading,
    loadMoreNextPage: fetchMore,
    hasNextPage = false,
  } = useInfiniteQuery(AudienceV2Document, {
    filterQueryKey: {
      releaseId,
      tier,
      sort,
    },
    getNextPageParam({ data }) {
      return (
        data.release?.collectors.pageInfo.hasNextPage && {
          after: data.release.collectors.pageInfo.endCursor,
        }
      );
    },
    variables({ pageParam }) {
      return {
        releaseId,
        pagination: {
          sort: getSortByTier(tier, sort),
          first: TABLET_MAX_SEATS_V2,
          after: pageParam?.after || null,
          tier,
        },
      };
    },
    list({ release }) {
      return release?.collectors.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    onError(error) {
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.AUDIENCE_FETCH_ERROR,
        error,
        errorType: ERROR_TYPE.FETCH_GQL_ERROR,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error calling useAudienceV2 hook',
        feature: FEATURES.AUDIENCE,
        indexedTags: {
          releaseId,
        },
      });
    },
    staleTime: 0,
  });

  const initialCollectors = useMemo(() => {
    return collectors.slice(0, maxSeats);
  }, [collectors, maxSeats]);

  return {
    collectors,
    initialCollectors,
    loading,
    hasNextPage,
    fetchMore,
    error,
  };
}
